<template>
  <div class="v2-color-picker">
    <div class="v2-color-picker__field">
      <label class="v2-color-picker__label">Color Family</label>
      <v2-dropdown
        :options="colorFamilyDropdownOptions"
        :value="colorFamilyValue"
        placeholder="Select color family"
        :searchable="true"
        :allow-create="true"
        @select="handleColorFamilySelect"
      />
    </div>
    <div class="v2-color-picker__field">
      <label class="v2-color-picker__label">
        Color
        <span class="v2-color-picker__required">*</span>
      </label>
      <validation-provider
        #default="{ errors }"
        :vid="`color-${_uid}`"
        name="Color"
        rules="required|min:1"
        immediate
      >
        <input
          v-model="color"
          class="v2-color-picker__input"
          :class="{ 'v2-color-picker__input--error': errors[0] }"
          placeholder="Enter color name"
          @input="syncColor"
          @blur="syncColor"
        >
        <span
          v-if="errors[0]"
          class="v2-color-picker__error"
        >{{ errors[0] }}</span>
      </validation-provider>
    </div>
  </div>
</template>

<script>
import { productConstants } from '@kingpin-global/kingpin-utils-frontend'
import { ValidationProvider } from 'vee-validate'
import store from '@/store'
import { UPDATE_PRODUCT_INFO } from '@/store/modules/productEdit.module'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'

export default {
  name: 'V2ColorPicker',
  components: {
    V2Dropdown,
    ValidationProvider,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    itemIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      color: '',
      colorFamilyValue: '',
      extraColorFamilies: [],
    }
  },
  computed: {
    colorFamilyDropdownOptions() {
      const base = productConstants.COLOR_FAMILY.map(c => ({ label: c, value: c }))
      const extra = this.extraColorFamilies.map(c => ({ label: c, value: c }))
      return [...base, ...extra]
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.color = newValue.color
        this.colorFamilyValue = newValue.colorFamily || ''
      },
      deep: true,
    },
  },
  methods: {
    syncColor() {
      this.emitUpdate({
        color: this.color,
        colorFamily: this.colorFamilyValue,
      })
    },
    _isKnownColorFamily(val) {
      return productConstants.COLOR_FAMILY.includes(val) || this.extraColorFamilies.includes(val)
    },
    _registerCustomColorFamily(val) {
      if (!val || this._isKnownColorFamily(val)) return
      this.extraColorFamilies.push(val)
    },
    handleColorFamilySelect(val) {
      const normalizedVal = val || ''
      this.colorFamilyValue = normalizedVal
      this._registerCustomColorFamily(val)
      this.emitUpdate({ colorFamily: normalizedVal })
    },
    emitUpdate(updatedFields = {}) {
      const { color = this.color, colorFamily = '' } = updatedFields

      this.colorFamilyValue = colorFamily

      this.updateColorState({ color, colorFamily }, updatedFields)
    },
    updateColorState({ color, colorFamily }, additionalFields = {}) {
      const items = this.$store.state.productEdit.newData.items
      if (items && items[this.itemIndex]) {
        const updatedItems = [...items]
        updatedItems[this.itemIndex] = {
          ...items[this.itemIndex],
          color,
          colorFamily,
        }

        store.commit(UPDATE_PRODUCT_INFO, {
          fieldName: 'items',
          newValue: updatedItems,
        })
      }

      const emitData = {
        ...this.value,
        color,
        colorFamily,
        ...additionalFields,
      }

      this.$emit('input', emitData)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-color-picker {
  display: flex;
  gap: var(--v2-space-4);
  margin-bottom: var(--v2-space-3);

  &__field {
    flex: 1;
    min-width: 0;
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-1);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__required {
    color: var(--v2-destructive);
  }

  &__input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-card);
    min-height: 38px;
    transition: border-color 0.15s ease;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__error {
    display: block;
    font-size: 0.6875rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }
}
</style>
